import {
  Backdrop,
  Box,
  FormControl,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { isFulfilled } from "@reduxjs/toolkit";
import { format, isValid } from "date-fns";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import DialogContentWrapper from "../../../../common/components/functional/dialogs/DialogContentWrapper";
import LoadingSpinner from "../../../../common/components/functional/loadingSpinner/LoadingSpinner";
import { TextFieldLight } from "../../../../common/components/styled/styled";
import {
  isoFormat,
  leadingZerosDateFormat,
} from "../../../../common/helpers/formatDateTime";
import isEmpty from "../../../../common/helpers/isEmpty";
import { Education, Talent } from "../../../talents/talentAPI";
import {
  selectTalentById,
  updateTalentEducations,
} from "../../../talents/talentsSlice";
import { UpdateTalentEducations } from "../../../talents/types/commands";
import { EditMode } from "../../TalentDetails";
import EducationLevels from "./EducationLevels";
import { UnsavedChangesProvider } from "../../../../contexts/UnsavedChangesContext";

type FormValues = {
  id: string;
  level: string;
  industry: string;
  degree: string;
  school: string;
  start: Date | null;
  end: Date | null;
};

type Props = {
  editMode: EditMode;
  onClose: () => void;
};

const TalentEditEducation = ({ onClose, editMode }: Props) => {
  const { t } = useTranslation();

  const { talentId } = useParams();
  const talent = useAppSelector(
    (state) => selectTalentById(state, talentId as string) as Talent,
  );
  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [selectedEducation] = useState<Education>(
    talent.educations?.values?.find(
      (education) => education.id === editMode.id,
    ) as Education,
  );

  const defaultValues: FormValues = {
    id: selectedEducation.id,
    level: selectedEducation.level,
    industry: selectedEducation.industry,
    degree: selectedEducation.degree,
    school: selectedEducation.school,
    start: new Date(selectedEducation.start),
    end: !!selectedEducation.end ? new Date(selectedEducation.end) : null,
  };

  const methods = useForm<FormValues>({
    defaultValues: defaultValues,
    mode: "onChange",
  });

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isDirty, isValid: isValidForm },
  } = methods;
  const start = watch("start");

  const onSubmit = (formValues: FormValues) => {
    setIsLoading(true);

    const isoStart = format(formValues.start!!, isoFormat);
    const isoEnd = !!formValues.end
      ? format(formValues.end, isoFormat)
      : undefined;

    if (talentId) {
      const existingEducations = talent.educations!!;

      const updatedEducations: Education[] = existingEducations.values.map(
        (education) => {
          if (education.id !== formValues.id) return education;

          return {
            id: education.id,
            level: formValues.level,
            industry: formValues.industry,
            degree: formValues.degree,
            school: formValues.school,
            start: isoStart,
            end: isoEnd,
          };
        },
      );

      const commandPayload: UpdateTalentEducations = {
        talentId: talent.id,
        version: existingEducations.version,
        educations: updatedEducations,
      };

      dispatch(updateTalentEducations(commandPayload)).then((action) => {
        setIsLoading(false);
        onClose();
        return isFulfilled(action);
      });
    }
  };

  const onSave = () => handleSubmit(onSubmit)();
  const onCancel = () => onClose();

  const selectionFor = (level: string) => (
    <MenuItem key={level} value={level}>
      {t("education:" + level)}
    </MenuItem>
  );

  const saveDisabled =
    !isEmpty(errors) || !isDirty || isLoading || !isValidForm;

  // Create an array of objects with original level names and translated names
  const sortedEducationLevels = EducationLevels.map((level) => ({
    originalName: level,
    translatedName: t("education:" + level),
  })).sort((a, b) => a.translatedName.localeCompare(b.translatedName)); // Sort based on translated names

  return (
    <DialogContentWrapper
      title={t("education:education")}
      margin
      saveDisabled={saveDisabled}
      onSave={onSave}
      onCancel={onCancel}
    >
      <Backdrop
        open={isLoading}
        sx={{
          backgroundColor: "rgba(255, 255, 255, 0.4)",
          zIndex: 1000,
        }}
      >
        <LoadingSpinner />
      </Backdrop>
      <UnsavedChangesProvider isDirty={methods.formState.isDirty}>
        <Grid
          item
          container
          xs={3}
          justifyContent="flex-end"
          alignItems="center"
        >
          <Typography textAlign="end" variant="secondary">
            {t("education:degreeName")}
          </Typography>
        </Grid>
        <Grid item container xs={9} alignItems="center">
          <Controller
            name="degree"
            control={control}
            rules={{ required: true }}
            render={({ field: { name, value, onChange } }) => (
              <Grid item container xs={9}>
                <TextField
                  autoComplete="off"
                  fullWidth
                  name={name}
                  size="small"
                  value={value}
                  onChange={onChange}
                ></TextField>
              </Grid>
            )}
          />
        </Grid>
        <Grid
          item
          container
          xs={3}
          justifyContent="flex-end"
          alignItems="center"
        >
          <Typography textAlign="end" variant="secondary">
            {t("education:school")}
          </Typography>
        </Grid>
        <Grid item container xs={9} alignItems="center">
          <Controller
            name="school"
            control={control}
            rules={{ required: true }}
            render={({ field: { name, value, onChange } }) => (
              <Grid item container xs={9}>
                <TextField
                  autoComplete="off"
                  fullWidth
                  name={name}
                  size="small"
                  value={value}
                  onChange={onChange}
                ></TextField>
              </Grid>
            )}
          />
        </Grid>
        <Grid
          item
          container
          xs={3}
          justifyContent="flex-end"
          alignItems="center"
        >
          <Typography textAlign="end" variant="secondary">
            {t("education:level")}
          </Typography>
        </Grid>
        <Grid item container xs={9} alignItems="center">
          <Controller
            name="level"
            control={control}
            rules={{ required: true }}
            render={({ field: { name, value, onChange } }) => (
              <Grid item container xs={9} alignItems="center">
                <FormControl fullWidth size="small">
                  <Select value={value} onChange={onChange}>
                    {sortedEducationLevels.map((levelObj) =>
                      selectionFor(levelObj.originalName),
                    )}
                  </Select>
                </FormControl>
              </Grid>
            )}
          />
        </Grid>
        <Grid
          item
          container
          xs={3}
          justifyContent="flex-end"
          alignItems="center"
        >
          <Typography textAlign="end" variant="secondary">
            {t("education:industry")}
          </Typography>
        </Grid>
        <Grid item container xs={9} alignItems="center">
          <Controller
            name="industry"
            control={control}
            rules={{ required: true }}
            render={({ field: { name, value, onChange } }) => (
              <Grid item container xs={9}>
                <TextField
                  autoComplete="off"
                  fullWidth
                  name={name}
                  size="small"
                  value={value}
                  onChange={onChange}
                ></TextField>
              </Grid>
            )}
          />
        </Grid>
        <Grid
          item
          container
          xs={3}
          justifyContent="flex-end"
          alignItems="center"
        >
          <Typography textAlign="end" variant="secondary">
            {t("education:date")}
          </Typography>
        </Grid>
        <Grid item container xs={9} alignItems="center">
          <Controller
            name="start"
            control={control}
            rules={{
              required: { value: true, message: t("common:requiredField") },
            }}
            render={({ field }) => (
              <DesktopDatePicker
                {...field}
                value={field.value}
                format={leadingZerosDateFormat}
                onChange={(newDate: Date | null) => field.onChange(newDate)}
                slots={{ textField: TextFieldLight }}
                slotProps={{
                  textField: {
                    size: "small",
                    sx: { input: { textAlign: "center" }, width: "135px" },
                    error: !!errors.start,
                    //helperText: errors.start?.message FIXME: Alignment
                  },
                }}
              />
            )}
          />
          <Box sx={{ mx: 1 }}>-</Box>
          <Controller
            name="end"
            control={control}
            rules={{
              validate: {
                validate: (value) => {
                  const endValue = value && value?.getTime();
                  const startValue = start?.getTime();

                  // if end value is gived, it should be a valid date
                  if (
                    endValue !== null &&
                    isNaN(endValue) &&
                    !isValid(endValue)
                  )
                    return false;

                  // end value can be empty
                  if (startValue && !endValue) return true;

                  // if end value is given, it should be after start value
                  return !!(startValue && endValue && startValue < endValue);
                },
              },
            }}
            render={({ field }) => (
              <DesktopDatePicker
                {...field}
                value={field.value}
                format={leadingZerosDateFormat}
                onChange={(newDate: Date | null) => field.onChange(newDate)}
                slots={{ textField: TextFieldLight }}
                slotProps={{
                  textField: {
                    size: "small",
                    sx: { input: { textAlign: "center" }, width: "135px" },
                    error: !!errors.end,
                    //helperTexterrors.end && t("common:invalidDate") FIXME: Alignment
                  },
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={3} />
        <Grid
          item
          container
          xs={9}
          justifyContent="flex-start"
          alignItems="center"
        >
          <Grid item container alignItems="center">
            <Controller
              control={control}
              name="end"
              render={({ field: { onChange, value } }) => (
                <>
                  <Checkbox
                    sx={{
                      "&.MuiCheckbox-root": {
                        paddingLeft: "0px",
                      },
                    }}
                    checked={!value}
                    onChange={(_, checked) =>
                      onChange(checked ? null : defaultValues.end)
                    }
                  />
                  <Typography>{t("education:ongoing")}</Typography>
                </>
              )}
            />
          </Grid>
        </Grid>
      </UnsavedChangesProvider>
    </DialogContentWrapper>
  );
};

export default TalentEditEducation;
